import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {AppConfigurationService} from '@shared/app-configuration.service';
import {CollaborativeWorkProject} from '@model/collaborative-work-project';
import {Observable} from 'rxjs';
import {CollaborativeWorkPerformance} from '@model/collaborative-work-performance';
import {StaffMemberCollaborativeWorkPerformance} from '@model/staffMember-collaborative-work-performance';
import {getDateString} from '@shared/util/date-util';

@Injectable({
  providedIn: 'root'
})
export class CollaborativeWorkService {

  private readonly _apiUrl: string;

  constructor(private _http: HttpClient, private _appConfig: AppConfigurationService) {
    this._apiUrl = `${this._appConfig.getConfig().apiUrl}/schools`;
  }

  loadCollaborativeWorkProjects(schoolId: string, schoolYear: number, includeDuration: boolean = false): Observable<CollaborativeWorkProject[]> {
    const parameters = new HttpParams()
      .set('year', `${schoolYear}`)
      .set('includeDuration', `${includeDuration}`);
    return this._http.get<CollaborativeWorkProject[]>(`${this._apiUrl}/${schoolId}/collaborativeWorkProjects`, {params: parameters});
  }

  saveCollaborativeWorkProject(schoolId: string, collaborativeWorkProject: CollaborativeWorkProject): Observable<CollaborativeWorkProject> {
    if (collaborativeWorkProject.id) {
      return this._http.put<CollaborativeWorkProject>(`${this._apiUrl}/${schoolId}/collaborativeWorkProjects/${collaborativeWorkProject.id}`,
        collaborativeWorkProject);
    } else {
      return this._http.post<CollaborativeWorkProject>(`${this._apiUrl}/${schoolId}/collaborativeWorkProjects`, collaborativeWorkProject);
    }
  }

  loadCollaborativeWorkPerformances(schoolId: string, staffMemberId: number, schoolYear: number): Observable<CollaborativeWorkPerformance[]> {
    const params = new HttpParams()
      .set('schoolYear', `${schoolYear}`);
    return this._http.get<CollaborativeWorkPerformance[]>(`${this._apiUrl}/${schoolId}/collaborativeWorkProjectPerformances/${staffMemberId}`, {params});
  }

  loadCollaborativeWorkPerformancesByProject(schoolId: string, projectId: number): Observable<CollaborativeWorkPerformance[]> {
    return this._http.get<CollaborativeWorkPerformance[]>(`${this._apiUrl}/${schoolId}/collaborativeWorkProjects/${projectId}/performances`);
  }

  savePerformance(schoolId: string, projectId: number, collaborativeWorkPerformance: CollaborativeWorkPerformance) {
    if (!!collaborativeWorkPerformance.id) {
      return this._http.put<CollaborativeWorkPerformance>(`${this._apiUrl}/${schoolId}/collaborativeWorkProjects/${projectId}/performances/${collaborativeWorkPerformance.id}`,
        {
          ...collaborativeWorkPerformance,
          date: getDateString(collaborativeWorkPerformance.date)
        });
    } else {
      return this._http.post<CollaborativeWorkPerformance>(`${this._apiUrl}/${schoolId}/collaborativeWorkProjects/${projectId}/performances`,
        {
          ...collaborativeWorkPerformance,
          date: getDateString(collaborativeWorkPerformance.date)
        });
    }
  }

  deletePerformance(schoolId: string, projectId: number, performanceId: number) {
    return this._http.delete(`${this._apiUrl}/${schoolId}/collaborativeWorkProjects/${projectId}/performances/${performanceId}`);
  }

  loadStaffMemberCollaborativeWorkProjectPerformances(schoolId: string, schoolYear: number): Observable<StaffMemberCollaborativeWorkPerformance[]> {
    const params = new HttpParams()
      .set('schoolYear', `${schoolYear}`);
    return this._http.get<StaffMemberCollaborativeWorkPerformance[]>(`${this._apiUrl}/${schoolId}/staffMemberCollaborativeWorkProjectPerformances`, {params});
  }
}
